import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { Subject, noop } from 'rxjs';

@Component({
  selector: 'working-list-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: WorkingListSearchComponent,
      multi: true
    }
  ]
})
export class WorkingListSearchComponent<T> implements OnChanges, ControlValueAccessor {
  
  constructor(private changeDetectorRef: ChangeDetectorRef) { }

  @Input()
  minimumSearchLength = 2;

  @Input()
  loading: boolean;

  @Input()
  editable = true;

  @Input()
  autoFocus = false;

  @Output()
  selectItem = new EventEmitter<T>();

  @Output()
  setValidationErrors = new EventEmitter<ValidationErrors>();

  // @Output()
  // enteredSerial = new EventEmitter<string>();

  selectedItem: T;

  isFocused = false;
  isWaitingForRemoteData = false;
  invalidSelection = false;

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  /** prevInput is needed only for IE. This is because IE constantly emits on the input value of lytx-typeahead. So, on an empty input, IE
   * will cause selectItem to emit/load even when we don't want it too. With prevInput, we will emit on an empty input only if the previous
   * input was not also blank.
   */

  @ViewChild('typeaheadInput') el: ElementRef;

  private onChange: (value: string) => void;
  private notifyOnTouched = () => noop();

  ngOnChanges(changesObj: SimpleChanges) {
    if (changesObj['loading'] && !this.loading) {
      if (this.autoFocus) {
        setTimeout(() => this.el.nativeElement.focus(), 0);
      }
    }

    if (!this.editable) {
      this.invalidSelection = false;
    }
  }

  search($event) {
    const value = $event.target.value;
    if (value === '') {
      this.setSelectedItem(undefined);
    }

    this.onChange(value);
  }

  clearSearch(): void {
    this.el.nativeElement.value = '';
    this.onChange(null);
    this.setSelectedItem(undefined);
  }

  onBlur(): void {
    this.isFocused = false;
    if (!this.editable && !this.selectedItem && this.el.nativeElement.value.length) {
      this.setValidationErrors.emit({ noSelection: true });
      this.invalidSelection = true;
    }
    this.notifyOnTouched();
    this.isWaitingForRemoteData = false;
  }

  /**
   * Angular calls this to set up a callback function to be called when the value of the control changes.
   * @param fn a callback function to call when the value of the control changes
   */
  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  /**
   * Angular calls this to set up a callback function to be called when the control has been interacted with or "blurred".
   * @param fn a callback function to call when the control has been touched (or "blurred")
   */
  registerOnTouched(fn) {
    this.notifyOnTouched = fn;
  }

  /**
   * Angular calls this to set the value of the control when used with a form.
   * @param value the value for the control
   */
  writeValue(value: T) {
    this.selectedItem = value;
    if (!this.editable) {
      this.invalidSelection = false;
    }
  }

  /**
   * Updates the currently-selected value to the specified value.  Emits selectItem and calls the notifyOnChange callback.
   * @param value the value to select
   */
  private setSelectedItem(value: T) {
    this.selectedItem = value;
    this.selectItem.emit(this.selectedItem);
    if (!this.editable) {
      this.setValidationErrors.emit({ noSelection: false });
      this.invalidSelection = false;
    }
    // this.notifyOnChange(this.selectedItem);
  }
}
